/**
 * The click path: which body, quadrangle or cell a point of the frame
 * answers to, and the cursor's own hover answer against the same raycast.
 *
 * Reads the closure it was cut out of through `PickDeps`. The `atlasCamera`
 * probe members that read nothing outside this seam ride on `probe`, which
 * the composition root in src/atlas.ts folds back into the one object the
 * tools/preview/dojo arms read.
 */

import * as THREE from 'three';

import type { AtlasLive, AtlasState } from '../atlas';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import { isOnScreen, metresPerPixel } from './camera.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { DescentAnchorHandle } from './descentAnchor.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { HexViewHandle } from './hexView.ts';
import { HOVER_HALO_RIM_FRACTION, HOVER_HALO_RING_FRACTION } from './hoverHalo.ts';
import type { InjectsHandle } from './injects.ts';
import type { InputHandle } from './input';
import type { InspectorHandle } from './inspector';
import type { MountHandle } from './mount.ts';
import type { NavigationHandle } from './navigation.ts';
import type { OverlayViewHandle } from './overlayView.ts';
import { type SubPlot } from './plotField.ts';
import type { PlotGeometryHandle } from './plotGeometry.ts';
import type { PyramidViewHandle } from './pyramidView.ts';
import type { SelectionHandle } from './selection.ts';
import type { SiteChipsHandle } from './panels/siteChips.ts';
import { SHIP_MARKER_ID } from './shipMarker.ts';
import type { SurfaceRadiusHandle } from './surfaceRadius.ts';
import type { WorldVec } from '../world/frame';

export interface PickDeps {
  readonly mount: MountHandle;
  readonly hexViewSeam: HexViewHandle;
  readonly selection: SelectionHandle;
  readonly navigation: NavigationHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly focusRig: FocusRigHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly plotGeometry: PlotGeometryHandle;
  readonly overlayView: OverlayViewHandle;
  readonly cameraRig: CameraRigHandle;
  readonly bodyScene: BodySceneHandle;
  readonly injects: InjectsHandle;
  readonly live: AtlasLive;
  /** Built after this module, so atlas.ts hands it over through a getter. */
  readonly input: InputHandle;
  readonly inspector: InspectorHandle;
  readonly siteChips: SiteChipsHandle;
  readonly ORIGIN: WorldVec;
  readonly state: AtlasState;
  readonly tileGroup: THREE.Group;
  readonly drawnGroundHit: PyramidViewHandle['drawnGroundHit'];
  readonly _q: THREE.Quaternion;
  readonly _v: THREE.Vector3;
  own(fn: () => void): void;
}

export interface PickHandle {
  pickAt(e: MouseEvent): void;
  pickSitesAt(e: MouseEvent): boolean;
  latLonOfHit(point: THREE.Vector3): { lat: number; lon: number };
  /** The cursor's own answer, once a frame; called from the tick. */
  updateHover(): void;
  /** Shared with `groundPointAt`'s own raycast, mutated in place on purpose. */
  readonly ndc: THREE.Vector2;
  readonly raycaster: THREE.Raycaster;
  /** Where the ray now in `raycaster` meets the relief-displaced ground; undefined while none is displaced. */
  drawnGroundAtRay(): THREE.Vector3 | null | undefined;
  haloLegacyCap: boolean;
  readonly hoverBodyId: string | null;
  readonly hoverCellId: string | null;
  readonly hoverInside: boolean;
  hoverOff: boolean;
  hoverDirty: boolean;
  /** The `atlasCamera` members that read nothing outside this module. */
  readonly probe: {
    readonly pickTolerancePx: number;
    setPickDebug(
      next: { tierGate?: boolean; nearestOnly?: boolean; labelOff?: boolean },
    ): { tierGate: boolean; nearestOnly: boolean; labelOff: boolean };
    pickBody(clientX: number, clientY: number): string | null;
    pickCellAtNdc(x: number, y: number): string | null;
    /** Where a body-fixed point on the open body's datum lands in the page, and whether it faces the eye. */
    clientOfLatLon(lat: number, lon: number): { x: number; y: number; front: boolean } | null;
  };
}

export function createPick(deps: PickDeps): PickHandle {
  const { inspector, ORIGIN, state, tileGroup, _q, _v, own } = deps;
  const { camera, cameraWorld, surface } = deps.mount;
  const { hexOverlay } = deps.hexViewSeam;
  const { applySelection, syncMarks } = deps.selection;
  const { applyState, focusShip, zoomIntoBody, zoomIntoCell } = deps.navigation;
  const { availableQuad } = deps.descentAnchorHandle;
  const { band } = deps.focusRig;
  const { bodyMesh, bodyQuads, descendable, ladderBody, quadFor } = deps.bodyReaders;
  const { groundDatumM } = deps.surfaceRadius;
  const { pickSubPlotOnTiles } = deps.plotGeometry;
  const { renderOverlay } = deps.overlayView;
  const { surfaceRegime } = deps.cameraRig;

  // Raycasting
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  const _drawnHit = new THREE.Vector3();
  const _datum = new THREE.Sphere();
  const _hit = new THREE.Vector3();

  /**
   * How far off a body's drawn disc a click still counts, pixels.
   *
   * Out here every body is drawn at `MIN_DRAWN_ANGULAR_DEG`, which is a disc
   * about two pixels across: an exact-hit raycast asks the player to land a
   * click inside a two-pixel dot.
   */
  const PICK_TOLERANCE_PX = 12;
  /** Half the side of the box a site-finder globe pick lists the sites under: 48 px across, at the nadir. */
  const SITE_PICK_HALF_SIDE_PX = 24;
  const _llPoint = new THREE.Vector3();
  const _llEye = new THREE.Vector3();
  const _llUp = new THREE.Vector3();

  /**
   * The body nearest the pointer, within the tolerance.
   *
   * Screen space, not a ray, so it has no depth of its own: where the pointer
   * is inside two discs the SMALLER apparent one wins, or Sol - drawn at the
   * same floor as everything else and sitting behind the inner system - would
   * take every click near a planet in front of it.
   */
  function pickNearestBody(x: number, y: number, systemOnly: boolean): string | null {
    if (!deps.bodyScene.state.orrery) return null;
    const rect = surface.getBoundingClientRect();
    const halfW = Math.max(1, rect.width) * 0.5;
    const halfH = Math.max(1, rect.height) * 0.5;
    // Pixels per degree at the centre of the frame, which is close enough for
    // a 12-pixel tolerance.
    const pxPerDeg = Math.max(1, rect.height) / camera.fov;
    camera.updateMatrixWorld();
    let bestId: string | null = null;
    let bestSlack = Infinity;
    let bestRadiusPx = Infinity;
    const consider = (id: string, mesh: THREE.Object3D, drawnDeg: number): void => {
      if (!mesh.visible || !isOnScreen(camera, mesh.position)) return;
      const p = _v.copy(mesh.position).project(camera);
      const radiusPx = drawnDeg * 0.5 * pxPerDeg;
      const slack = Math.hypot((p.x - x) * halfW, (p.y - y) * halfH) - radiusPx;
      if (slack > PICK_TOLERANCE_PX) return;
      const better = slack <= 0 && bestSlack <= 0 ? radiusPx < bestRadiusPx : slack < bestSlack;
      if (!better) return;
      bestId = id;
      bestSlack = slack;
      bestRadiusPx = radiusPx;
    };
    for (const b of deps.bodyScene.state.orrery.bodies) {
      if (systemOnly && b.row.pick_band !== 'SYSTEM') continue;
      consider(b.row.id, b.mesh, (b.mesh.userData['drawnAngularDiameterDeg'] as number) ?? 0);
    }
    consider('sol', deps.bodyScene.state.orrery.sol.mesh, deps.bodyScene.state.orrery.sol.state.drawnAngularDiameterDeg);
    return bestId;
  }

  /**
   * Whether the pointer is on the ship marker's own reticle.
   *
   * Separate from the body sweep above, because the marker is not a body and
   * the rule it is picked under is not the one that arbitrates two discs: it is
   * drawn over everything and it is the biggest thing on the pane, so it takes
   * the pixels no body has already claimed and never the ones a body has.
   */
  function pickHitsShip(x: number, y: number): boolean {
    if (deps.bodyScene.state.shipMarker === null || deps.injects.state.shipPickOff) return false;
    if (!deps.bodyScene.state.shipMarker.object.visible || !(deps.bodyScene.state.shipMarker.drawnAngularDiameterDeg > 0)) return false;
    if (!isOnScreen(camera, deps.bodyScene.state.shipMarker.object.position)) return false;
    const rect = surface.getBoundingClientRect();
    const halfW = Math.max(1, rect.width) * 0.5;
    const halfH = Math.max(1, rect.height) * 0.5;
    const pxPerDeg = Math.max(1, rect.height) / camera.fov;
    camera.updateMatrixWorld();
    const p = _v.copy(deps.bodyScene.state.shipMarker.object.position).project(camera);
    const radiusPx = deps.bodyScene.state.shipMarker.drawnAngularDiameterDeg * 0.5 * pxPerDeg;
    return Math.hypot((p.x - x) * halfW, (p.y - y) * halfH) <= radiusPx + PICK_TOLERANCE_PX;
  }

  /** `descent-dojo --manifest --inject pick-nearest`: the pre-fix rule back. */
  let pickNearestOnly = false;
  /** `descent-dojo --namepick --inject name-inert`: a name that answers nothing. */
  let pickLabelOff = false;

  const _pickChild = new THREE.Vector3();
  const _pickParent = new THREE.Vector3();

  /**
   * The same pick, with a moon that cannot be told from its primary handed up.
   *
   * From Mars, Pluto and Charon are 19,600 km apart, which is 0.00016 degrees,
   * and both are drawn at `MIN_DRAWN_ANGULAR_DEG`: the two discs are one dot,
   * and whichever answers is whichever centre happens to be nearer, so a click
   * on Pluto landed on Charon. Where the primary's own centre falls inside the
   * moon's drawn disc the moon is not separately resolvable at that distance
   * and the primary takes the click; the moon is reached from the primary's own
   * page. A moon with a dot of its own - Phobos from Mars at ORBIT, 377 px off
   * its primary - is past that test and keeps every click it answers.
   */
  function resolveToPrimary(id: string | null, x: number, y: number): string | null {
    if (id === null || !deps.bodyScene.state.orrery || pickNearestOnly) return id;
    const rect = surface.getBoundingClientRect();
    const halfW = Math.max(1, rect.width) * 0.5;
    const halfH = Math.max(1, rect.height) * 0.5;
    const pxPerDeg = Math.max(1, rect.height) / camera.fov;
    camera.updateMatrixWorld();
    let out = id;
    // The chain rather than one hop: a moon of a moon is not in the registry
    // today, and walking it costs nothing.
    for (let hops = 0; hops < 4; hops++) {
      const child = deps.bodyScene.state.orrery.byId(out);
      const parentId = child?.row.parent ?? null;
      if (!child || parentId === null) break;
      const parent = deps.bodyScene.state.orrery.byId(parentId);
      if (!parent || !parent.mesh.visible || !isOnScreen(camera, parent.mesh.position)) break;
      const childDeg = (child.mesh.userData['drawnAngularDiameterDeg'] as number) ?? 0;
      const parentDeg = (parent.mesh.userData['drawnAngularDiameterDeg'] as number) ?? 0;
      const c = _pickChild.copy(child.mesh.position).project(camera);
      const p = _pickParent.copy(parent.mesh.position).project(camera);
      const sepPx = Math.hypot((c.x - p.x) * halfW, (c.y - p.y) * halfH);
      if (sepPx > childDeg * 0.5 * pxPerDeg) break;
      // And the primary has to be under the cursor itself, to the same
      // tolerance the pick already grants a floored dot.
      const slack = Math.hypot((p.x - x) * halfW, (p.y - y) * halfH) - parentDeg * 0.5 * pxPerDeg;
      if (slack > PICK_TOLERANCE_PX) break;
      out = parentId;
    }
    return out;
  }

  /**
   * Which body is under this point of the frame.
   *
   * The one body pick in the Atlas: the click path calls it and so does the
   * descent dojo's `pick` criterion, through `scene.userData.atlasCamera`, so
   * the instrument cannot pass against a raycast the player never runs.
   *
   * An exact hit answers first, so a large disc behaves as it always has; only
   * where the ray hits nothing does the pixel tolerance run, and only in the
   * two bands where a body can be a floored dot. Either answer goes through
   * `resolveToPrimary`, so a moon drawn on top of its primary cannot take the
   * click by way of the ray any more than by way of the tolerance.
   */
  function pickBodyAtNdc(x: number, y: number): string | null {
    if (!deps.bodyScene.state.orrery) return null;
    ndc.set(x, y);
    raycaster.setFromCamera(ndc, camera);
    // `pick_band` is what the registry says about where a body can be picked,
    // and at SYSTEM it is load-bearing: Luna's floored dot is four thousandths
    // of a degree from Earth's from 127 AU out, so with satellites in the list
    // the ray at Earth's own pixel comes back holding the Moon.
    const bandNow = band();
    const systemOnly = bandNow === 'SYSTEM';
    const targets: THREE.Object3D[] = deps.bodyScene.state.orrery.bodies
      .filter((b) => !systemOnly || b.row.pick_band === 'SYSTEM')
      .map((b) => b.mesh);
    targets.push(deps.bodyScene.state.orrery.sol.mesh);
    const hits = raycaster.intersectObjects(targets, false);
    const hit = hits[0];
    if (hit) {
      return resolveToPrimary((hit.object.userData['bodyId'] as string | undefined) ?? null, x, y);
    }
    if (bandNow !== 'SYSTEM' && bandNow !== 'ORBIT') return null;
    const near = resolveToPrimary(pickNearestBody(x, y, systemOnly), x, y);
    if (near !== null) return near;
    // The name next, and only where nothing else answered: a body out here is a
    // two-pixel dot with its name standing 18.6 px clear above it, past the
    // tolerance above, so the one thing the eye can aim at answered nothing.
    // The `pick_band` filter does not apply to a name - it holds a moon out of
    // the dot pick because a moon's dot cannot be told from its primary's, and
    // a moon's name is drawn only once the declutter has cleared it of that.
    const named = pickLabelOff ? null : deps.bodyScene.state.orrery.pickLabelAtNdc(x, y);
    if (named !== null) return named;
    // The marker last, after every dot and every name. It is the largest thing
    // on the pane and it is drawn over all of them, but out at the heliocentric
    // view it sits on whatever the ship is orbiting, and a reticle 2.5 degrees
    // across must not take the click off the dot and the name inside it. What
    // is left is the ring, which is what the eye aims at anyway.
    return pickHitsShip(x, y) ? SHIP_MARKER_ID : null;
  }

  /**
   * A hit on Mars as a body-fixed lat/lon.
   *
   * Through the body's own rotation, not the scene axes: the planet turns on the
   * WGCCRE pole now, so the naive asin(y) would drift a degree an hour.
   */
  function latLonOfHit(point: THREE.Vector3): { lat: number; lon: number } {
    const node = deps.bodyScene.state.bodyNode;
    if (!node) return { lat: 0, lon: 0 };
    _v.copy(point).sub(node.mesh.position)
      .applyQuaternion(_q.copy(node.quaternion).invert())
      .normalize();
    const lat = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(_v.y, -1, 1)));
    let lon = THREE.MathUtils.radToDeg(Math.atan2(-_v.z, _v.x));
    if (lon < 0) lon += 360;
    return { lat, lon };
  }

  /**
   * Where the ray now in `raycaster` meets the ground, as a body-fixed lat/lon.
   *
   * The globe is a sphere and a facet of it sags under the datum - 16 km on
   * Mars at the 32 by 24 it was cut at when this was found, a ninth of that at
   * `GLOBE_WIDTH_SEGMENTS` by `GLOBE_HEIGHT_SEGMENTS`; at the oblique framing
   * the ORBITAL rung takes, 16 km moved a triangle hit about 9 km across the
   * ground - wider than a claim cell - so
   * the cell under the cursor and the cell the pick returned were different
   * cells and the descent gate never opened. The mesh answers WHETHER the body
   * was hit, and the datum sphere the overlay draws on answers WHERE.
   */
  /**
   * Where the ray now in `raycaster` meets the relief-displaced ground, scene
   * axes; undefined while no relief is displaced and the position buffers are the ground.
   */
  function drawnGroundAtRay(): THREE.Vector3 | null | undefined {
    const { origin, direction } = raycaster.ray;
    const t = deps.drawnGroundHit(
      { x: origin.x + cameraWorld.x, y: origin.y + cameraWorld.y, z: origin.z + cameraWorld.z },
      direction,
      raycaster.far,
    );
    return t === null || t === undefined ? t : _drawnHit.copy(origin).addScaledVector(direction, t);
  }

  function groundLatLonAtRay(node: THREE.Mesh): { lat: number; lon: number } | null {
    const hits = raycaster.intersectObjects([node], false);
    if (!hits[0]) return null;
    const radiusM = deps.injects.state.pickFacetGate ? 0 : groundDatumM(ladderBody());
    // The drawn LEAVES first, wherever they are up: the datum sphere is no
    // more the ground than the globe's facets were. At a band's own shallow
    // pitch the ray meets the surface at a grazing angle, so the kilometres
    // Hadley's ground stands over the datum moved the picked cell off the
    // marker's own at LOCAL and CLAIM. Off the same surface the grid drapes
    // onto, so a click and the affordance under it agree.
    if (radiusM > 0 && !deps.injects.state.pickDatumGate && tileGroup.visible && tileGroup.children.length > 0) {
      const drawn = drawnGroundAtRay();
      if (drawn) return latLonOfHit(drawn);
      const onGround = drawn === undefined ? raycaster.intersectObjects(tileGroup.children, false) : [];
      if (onGround[0]) return latLonOfHit(onGround[0].point);
    }
    const datum = radiusM > 0
      ? raycaster.ray.intersectSphere(_datum.set(node.position, radiusM), _hit)
      : null;
    return latLonOfHit(datum ?? hits[0].point);
  }

  /**
   * The cell under a point of the frame, the way the click path reads it.
   *
   * Exposed the way `pickBodyAtNdc` is, and for the same reason: an instrument
   * that agrees with a raycast the player never runs is not a reading.
   * `descent-dojo --hex`'s `marker` bar asks it at the anchor marker's own
   * pixel.
   */
  function pickCellAtNdc(x: number, y: number): string | null {
    const node = bodyMesh();
    if (!node) return null;
    ndc.set(x, y);
    raycaster.setFromCamera(ndc, camera);
    const ground = groundLatLonAtRay(node);
    return ground ? hexOverlay.pick(ground.lat, ground.lon) : null;
  }

  /**
   * The site finder's armed pick: the sites under a box round the clicked
   * ground, at BODY or QUADRANGLE. True when the click was the finder's.
   */
  function pickSitesAt(e: MouseEvent): boolean {
    const node = bodyMesh();
    if (!deps.siteChips.globePickArmed || !(state.tier === 1 || state.tier === 2) || !node) return false;
    const rect = surface.getBoundingClientRect();
    ndc.set(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
    raycaster.setFromCamera(ndc, camera);
    const ground = groundLatLonAtRay(node);
    if (ground) {
      const mpp = metresPerPixel(deps.focusRig.altitude(), camera.fov, Math.max(1, rect.height));
      deps.siteChips.pickOnGlobe(ground.lat, ground.lon, Math.min(2000, Math.max(5, (SITE_PICK_HALF_SIDE_PX * mpp) / 1000)));
    }
    return true;
  }

  function pickAt(e: MouseEvent): void {
    if (pickSitesAt(e)) return;
    const rect = surface.getBoundingClientRect();
    ndc.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
    ndc.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
    raycaster.setFromCamera(ndc, camera);
    // The next pick is what dismisses the not-in-slice card, so it goes before
    // any branch below can raise a new one.
    if (inspector.clearFlash()) renderOverlay();

    if (state.tier === 0) {
      const id = pickBodyAtNdc(ndc.x, ndc.y);
      if (id === SHIP_MARKER_ID) { focusShip(); return; }
      if (id) zoomIntoBody(id);
      return;
    }
    // Below the heliocentric rung a body drawn in space used to be inert while
    // another held the focus; the ladder's own body still descends instead.
    if (!deps.injects.state.pickTierGate) {
      const other = pickBodyAtNdc(ndc.x, ndc.y);
      if (other !== null && other !== ladderBody()) {
        zoomIntoBody(other);
        return;
      }
    }
    const node = bodyMesh();
    if (state.tier === 1 && descendable() && bodyQuads().length > 0 && node) {
      // Click anywhere on the body at T1 -> reveal its quadrangle layer.
      // No lat/lon snap; the player picks a quad visually at T2.
      const hits = raycaster.intersectObjects([node], false);
      if (hits[0]) {
        state.tier = 2; state.quadId = null; state.cellId = null;
        applyState();
      }
      return;
    }
    if (state.tier === 2 && descendable() && node) {
      const ground = groundLatLonAtRay(node);
      if (ground) {
        const { lat, lon } = ground;
        const quad = quadFor(lat, lon);
        if (quad && quad.id === availableQuad()?.id) {
          state.tier = 3; state.quadId = quad.id;
          state.cellId = hexOverlay.anchorCell(); applyState();
        } else if (quad) {
          inspector.flashInspector(`${quad.id} ${quad.name} is catalogued, with no baked terrain in this slice.`);
        }
      }
      return;
    }
    if ((state.tier === 3 || state.tier === 4) && descendable()) {
      // The cell under the cursor, not the nearest centre in raw degrees: a
      // squared-degree distance is wrong away from the equator, and there is no
      // list of cells to be nearest to any more. H3 answers containment
      // directly at whatever resolution the altitude has selected.
      if (!node) return;
      const ground = groundLatLonAtRay(node);
      if (!ground) return;
      const picked = hexOverlay.pick(ground.lat, ground.lon);
      if (!picked) return;
      const isAnchor = picked === hexOverlay.anchorCell();
      if (isAnchor) {
        state.tier = state.tier === 3 ? 4 : 5;
        state.cellId = picked;
        applyState();
      } else {
        zoomIntoCell(picked);
      }
      return;
    }
    if (state.tier >= 5) {
      // At CLAIM and PLOT the cell is picked on the SCREEN rather than by a ray
      // against the ground: every surveyed cell's own world position is
      // projected - its height read from the resident leaf under it - and the
      // nearest one inside its own projected footprint wins. A ray against the
      // draw set misses wherever a level is still being meshed, and a ray
      // against the datum sphere misses by the local relief; projecting the
      // cells uses the same frame the camera is placed in, so it cannot
      // disagree with what is on screen. The whole-file mesh answers for itself
      // only in the walkaround, where it is the surface.
      const hit = deps.live.plot && surfaceRegime()
        ? deps.live.plot.pick(raycaster.ray.origin, raycaster.ray.direction)
        : pickSubPlotOnTiles();
      if (!hit && !deps.live.plot && !deps.live.plotSubs.length) return;
      // Clicking the selected cell again lets it go. There was no way to put a
      // selection down once picked up: every click inside a plot lands on some
      // cell, so `selectedSub = hit` could only ever move the highlight, never
      // clear it — and the highlight drags a lit hex and a 172 m solar array
      // around with it.
      deps.live.selectedSub = hit && deps.live.selectedSub && hit.id === deps.live.selectedSub.id ? null : hit;
      applySelection();
      renderOverlay();
      return;
    }
  }

  /**
   * How wide the halo is drawn round a body's own disc, and the floor under it.
   *
   * Out at the heliocentric rungs every body is drawn at one floored angular
   * size, about two pixels, so a ring proportional to the disc alone would be
   * two pixels too. The ceiling is what keeps it a ring rather than a wash once
   * a body is close enough to fill the pane.
   */
  const HOVER_HALO_SCALE = 2.6;
  const HOVER_HALO_MIN_DEG = 2.2;
  const HOVER_HALO_MAX_DEG = 20;
  /** Past this the rim's own sizing runs into tan's asymptote, and off frame. */
  const HOVER_HALO_RIM_MAX_DEG = 170;

  const haloDeg = (drawnDeg: number): number => Math.min(
    HOVER_HALO_MAX_DEG, Math.max(drawnDeg * HOVER_HALO_SCALE, HOVER_HALO_MIN_DEG));

  /**
   * The sprite that puts the rim's inner edge on a body's own limb.
   *
   * The sprite is a flat billboard sized by its half-span, so the fraction is
   * applied to the TANGENT and not to the angle: a body drawn 42 degrees across
   * is 21 degrees of half-angle, and a rim inner edge at three quarters of the
   * sprite lands on it at `atan(tan(21) / 0.75)`, 27.1 degrees of half-angle,
   * rather than at 28.
   */
  const rimSpriteDeg = (drawnDeg: number): number => {
    const half = Math.min(drawnDeg, HOVER_HALO_RIM_MAX_DEG) * 0.5 * (Math.PI / 180);
    return (2 * Math.atan(Math.tan(half) / HOVER_HALO_RIM_FRACTION)) / (Math.PI / 180);
  };

  /**
   * Ring or rim, and how wide, or null to draw none.
   *
   * The ring stops growing at the ceiling and the body does not, so past the
   * drawn size where the ring would land ON the limb it is a circle inside a
   * planet, enclosing nothing and touching nothing. From there the answer is
   * the body's own limb instead, which reads at any drawn size and needs no
   * second rule for a body wider than the frame. Below that size nothing about
   * the halo changes.
   */
  function hoverHaloAt(id: string): { at: WorldVec; deg: number; rim: boolean } | null {
    const sized = (at: WorldVec, drawnDeg: number): { at: WorldVec; deg: number; rim: boolean } => {
      const deg = haloDeg(drawnDeg);
      const rim = !haloLegacyCap && drawnDeg >= deg * HOVER_HALO_RING_FRACTION;
      return { at, deg: rim ? rimSpriteDeg(drawnDeg) : deg, rim };
    };
    if (id === SHIP_MARKER_ID) {
      const m = deps.bodyScene.state.shipMarker;
      if (m === null || !m.object.visible || !(m.drawnAngularDiameterDeg > 0)) return null;
      return sized(m.world, m.drawnAngularDiameterDeg);
    }
    if (!deps.bodyScene.state.orrery) return null;
    if (id === 'sol') {
      const deg = deps.bodyScene.state.orrery.sol.state.drawnAngularDiameterDeg;
      return deps.bodyScene.state.orrery.sol.mesh.visible && deg > 0 ? sized(ORIGIN, deg) : null;
    }
    const b = deps.bodyScene.state.orrery.byId(id);
    if (!b || !b.mesh.visible) return null;
    const deg = (b.mesh.userData['drawnAngularDiameterDeg'] as number | undefined) ?? 0;
    return deg > 0 ? sized(b.world, deg) : null;
  }

  /** What the cursor is on: a dot at the orrery rungs, a cell at the grid bands. */
  let hoverBodyId: string | null = null;
  let hoverCellId: string | null = null;
  /** The pointer in NDC, and whether it is over the pane at all. */
  let hoverNdcX = 0;
  let hoverNdcY = 0;
  let hoverInside = false;
  /**
   * Whether the answer under the cursor is worth asking for again.
   *
   * The ground pick casts a ray at the drawn tiles, which is not a thing to do
   * on every frame the cursor happens to be still for. It is asked when the
   * pointer moves and when the view under it changes, and not otherwise.
   */
  let hoverDirty = false;
  /** `descent-dojo --hover --inject hover-none`: a cursor nothing answers. */
  let hoverOff = false;
  /**
   * `descent-dojo --hover --inject halo-cap`: the ring held at the ceiling.
   *
   * The halo before it had a second shape - a ring that stops growing at 20
   * degrees over a body that does not, which is the circle drawn inside a
   * planet the rim replaced.
   */
  let haloLegacyCap = false;

  function setHoverTarget(bodyId: string | null, cellId: string | null, sub: SubPlot | null): void {
    const on = bodyId !== null || cellId !== null || sub !== null;
    const want = deps.input.isDragging ? 'grabbing' : on ? 'pointer' : 'grab';
    if (surface.style.cursor !== want) surface.style.cursor = want;
    const sameSub = (sub?.id ?? null) === (deps.live.hoverSub?.id ?? null);
    if (bodyId === hoverBodyId && cellId === hoverCellId && sameSub) return;
    hoverBodyId = bodyId;
    hoverCellId = cellId;
    deps.live.hoverSub = sub;
    hexOverlay.setHoverCell(cellId);
    syncMarks();
    deps.live.needsRender = true;
  }

  /** Ask what is under the cursor, against the same paths a click is answered by. */
  function readHoverTarget(): void {
    if (!hoverInside || deps.input.isDragging || hoverOff) { setHoverTarget(null, null, null); return; }
    ndc.set(hoverNdcX, hoverNdcY);
    raycaster.setFromCamera(ndc, camera);
    if (state.tier === 0) {
      setHoverTarget(pickBodyAtNdc(hoverNdcX, hoverNdcY), null, null);
      return;
    }
    // At the plot bands a surveyed cell is what the click takes, so it is what
    // the cursor answers with: same pick, same ground, one frame earlier.
    if (state.tier >= 5) {
      const sub = deps.live.plot && surfaceRegime()
        ? deps.live.plot.pick(raycaster.ray.origin, raycaster.ray.direction)
        : pickSubPlotOnTiles();
      setHoverTarget(null, null, sub);
      return;
    }
    // The two rungs a cell is what a click takes. The grid draws above them
    // too, but up there a click reveals a quadrangle and below them it picks a
    // plot inside a cell already held, so a cell lit under the cursor there
    // would be an affordance for something the click does not do.
    if (state.tier !== 3 && state.tier !== 4) { setHoverTarget(null, null, null); return; }
    const node = bodyMesh();
    if (node === null || !hexOverlay.object.visible) { setHoverTarget(null, null, null); return; }
    const ground = groundLatLonAtRay(node);
    setHoverTarget(null, ground === null ? null : hexOverlay.pick(ground.lat, ground.lon), null);
  }

  /**
   * The cursor's own answer, once a frame.
   *
   * The halo is placed every frame whether or not the answer changed: the body
   * it is round is moving, and a ring left at the last frame's point is a ring
   * beside the dot rather than on it.
   */
  function updateHover(): void {
    if (hoverDirty) { hoverDirty = false; readHoverTarget(); }
    const t = hoverBodyId === null ? null : hoverHaloAt(hoverBodyId);
    if (deps.bodyScene.state.hoverHalo?.show(t?.at ?? null, t?.deg ?? 0, cameraWorld, t?.rim ?? false)) {
      deps.live.needsRender = true;
    }
  }

  function onHoverMove(e: PointerEvent): void {
    const rect = surface.getBoundingClientRect();
    hoverNdcX = ((e.clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
    hoverNdcY = -((e.clientY - rect.top) / Math.max(1, rect.height)) * 2 + 1;
    hoverInside = true;
    hoverDirty = true;
    deps.live.needsRender = true;
  }
  function onHoverLeave(): void {
    hoverInside = false;
    hoverDirty = true;
    deps.live.needsRender = true;
  }
  surface.addEventListener('pointermove', onHoverMove);
  surface.addEventListener('pointerleave', onHoverLeave);
  own(() => {
    surface.removeEventListener('pointermove', onHoverMove);
    surface.removeEventListener('pointerleave', onHoverLeave);
  });

  return {
    pickAt,
    pickSitesAt,
    latLonOfHit,
    updateHover,
    ndc,
    raycaster,
    drawnGroundAtRay,
    get haloLegacyCap(): boolean { return haloLegacyCap; },
    set haloLegacyCap(v: boolean) { haloLegacyCap = v; },
    get hoverBodyId(): string | null { return hoverBodyId; },
    get hoverCellId(): string | null { return hoverCellId; },
    get hoverInside(): boolean { return hoverInside; },
    get hoverOff(): boolean { return hoverOff; },
    set hoverOff(v: boolean) { hoverOff = v; },
    get hoverDirty(): boolean { return hoverDirty; },
    set hoverDirty(v: boolean) { hoverDirty = v; },
    probe: {
      get pickTolerancePx(): number { return PICK_TOLERANCE_PX; },
      clientOfLatLon(lat: number, lon: number): { x: number; y: number; front: boolean } | null {
        const node = deps.bodyScene.state.bodyNode;
        const radiusM = groundDatumM(ladderBody());
        if (!node || !(radiusM > 0)) return null;
        const la = THREE.MathUtils.degToRad(lat);
        const lo = THREE.MathUtils.degToRad(lon);
        // The inverse of latLonOfHit: east longitude is atan2(-z, x) in the body's frame.
        _llUp.set(Math.cos(la) * Math.cos(lo), Math.sin(la), -Math.cos(la) * Math.sin(lo)).applyQuaternion(node.quaternion);
        _llPoint.copy(_llUp).multiplyScalar(radiusM).add(node.mesh.position);
        camera.updateMatrixWorld();
        camera.getWorldPosition(_llEye).sub(_llPoint);
        const front = _llEye.dot(_llUp) > 0;
        _llPoint.project(camera);
        const rect = surface.getBoundingClientRect();
        return { x: rect.left + (_llPoint.x * 0.5 + 0.5) * rect.width, y: rect.top + (-_llPoint.y * 0.5 + 0.5) * rect.height, front };
      },
      /** The pick's own controls: `pick-tier-gate`, `pick-nearest` and `name-inert`. */
      setPickDebug(
        next: { tierGate?: boolean; nearestOnly?: boolean; labelOff?: boolean },
      ): { tierGate: boolean; nearestOnly: boolean; labelOff: boolean } {
        if (next.tierGate !== undefined) deps.injects.state.pickTierGate = next.tierGate;
        if (next.nearestOnly !== undefined) pickNearestOnly = next.nearestOnly;
        if (next.labelOff !== undefined) pickLabelOff = next.labelOff;
        return { tierGate: deps.injects.state.pickTierGate, nearestOnly: pickNearestOnly, labelOff: pickLabelOff };
      },
      /** The click path's own raycast, with no side effect. */
      pickBody(clientX: number, clientY: number): string | null {
        const r = surface.getBoundingClientRect();
        return pickBodyAtNdc(
          ((clientX - r.left) / r.width) * 2 - 1,
          -((clientY - r.top) / r.height) * 2 + 1,
        );
      },
      /** The cell the click path picks at a point of the frame, for `--hex`. */
      pickCellAtNdc(x: number, y: number): string | null {
        return pickCellAtNdc(x, y);
      },
    },
  };
}
